/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { paragraphSmall, weightSemiBold } from 'src/components/css'
import { balanceLabel } from '../utils/balanceI18n'
import { IMPORT_GRID_COLUMNS } from '../constants/importGrid'

// Read SECONDARY_350 (absent from the typed appColors inference) via a cast,
// the pattern PlayerFormSkeleton uses for the same bar.
const palette = colors as Record<string, string>

/** Six rows: about what the bounded grid shows before it scrolls. */
const ROWS = 6

type SkeletonBarProps = {
  width: number | string
  height?: number
  radius?: number
}

/** One pulsing placeholder bar — the house pulse (RegistrationSeasonSkeleton). */
export const SkeletonBar = ({
  width,
  height = 12,
  radius = 6,
}: SkeletonBarProps) => (
  <span css={[barStyle, barSizeStyle(width, height, radius)]} />
)

/**
 * Jeff, Oct 1 — Import Players From Previous Seasons, while a season's players
 * load: the grid's own header and six rows of placeholders in its columns, so
 * nothing moves when the players land. Also what shows while the grid's code
 * loads (it is a separate chunk), so the two waits look like one.
 *
 * No AG Grid here: this renders before that chunk has arrived.
 */
const ImportPreviousSeasonsSkeleton = () => (
  <div css={boxStyle} aria-hidden='true' data-testid='import-skeleton'>
    <div css={[rowStyle, headerStyle]}>
      <span css={centredStyle}>
        <SkeletonBar width={18} height={18} radius={4} />
      </span>
      <span css={headerCellStyle}>
        {balanceLabel('common:balanceImportColPlayer')}
      </span>
      <span css={headerCellStyle}>
        {balanceLabel('common:balanceImportColPosition')}
      </span>
      <span css={headerCellStyle}>
        {balanceLabel('common:balanceImportColEmail')}
      </span>
    </div>
    {Array.from({ length: ROWS }, (_, index) => (
      <div key={index} css={[rowStyle, bodyRowStyle]}>
        <span css={centredStyle}>
          <SkeletonBar width={18} height={18} radius={4} />
        </span>
        <span css={playerStyle}>
          <SkeletonBar width={28} height={28} radius={14} />
          <SkeletonBar width={110 + ((index * 23) % 60)} />
        </span>
        <span css={cellStyle}>
          <SkeletonBar width={56} />
        </span>
        <span css={cellStyle}>
          <SkeletonBar width={150 + ((index * 31) % 70)} />
        </span>
      </div>
    ))}
  </div>
)

export default ImportPreviousSeasonsSkeleton

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

const barStyle = css`
  display: block;
  flex-shrink: 0;
  max-width: 100%;
  background-color: ${palette.SECONDARY_350};
  animation: ${pulse} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

// Each bar's own size: they differ per row so the placeholders read as names.
const barSizeStyle = (
  width: number | string,
  height: number,
  radius: number
) => css`
  width: ${typeof width === 'number' ? `${width}px` : width};
  height: ${height}px;
  border-radius: ${radius}px;
`

/** The grid's frame: the same 1px border and 8px corners as DataGrid's theme. */
const boxStyle = css`
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  overflow: hidden;
  background: ${colors.SECONDARY_300};
`

const rowStyle = css`
  display: grid;
  grid-template-columns: ${IMPORT_GRID_COLUMNS};
  align-items: center;
`

const headerStyle = css`
  height: 40px;
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const headerCellStyle = css`
  ${paragraphSmall};
  ${weightSemiBold};
  padding: 0 12px;
  color: ${colors.NEUTRAL_0};
`

const bodyRowStyle = css`
  height: 48px;
  border-top: 1px solid ${colors.SECONDARY_200};

  &:nth-of-type(odd) {
    background: ${colors.SECONDARY_400};
  }
`

const centredStyle = css`
  display: flex;
  justify-content: center;
`

const cellStyle = css`
  display: flex;
  padding: 0 12px;
  min-width: 0;
`

const playerStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  min-width: 0;
`
